import { motion } from "framer-motion";
import { layoutSpring } from "../motion";

interface Option<T extends string> {
  value: T;
  label: string;
}

interface SegmentedControlProps<T extends string> {
  options: readonly Option<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  /**
   * Unique per control instance. Reused as the Framer Motion layoutId so the
   * accent highlight slides between options; sharing it across controls would
   * make the highlight jump between them.
   */
  layoutId: string;
  className?: string;
  style?: React.CSSProperties;
  /** Apply whiteSpace: nowrap to each button (use when labels may wrap). */
  nowrap?: boolean;
}

/**
 * Segmented filter control with a sliding accent highlight. The highlight is a
 * single motion element keyed by `layoutId` rendered only on the active option;
 * Framer Motion animates it from the previous option's rect to the new one.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  layoutId,
  className,
  style,
  nowrap,
}: SegmentedControlProps<T>) {
  return (
    <div className={`segment${className ? ` ${className}` : ""}`} role="group" aria-label={ariaLabel} style={style}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            className={active ? "active" : ""}
            onClick={() => onChange(o.value)}
            style={{ padding: "0 var(--space-3)", whiteSpace: nowrap ? "nowrap" : undefined }}
          >
            {active ? <motion.span layoutId={layoutId} className="segment-highlight" transition={layoutSpring} /> : null}
            <span className="segment-label">{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}
